import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { t } from 'i18next';
import { Trans } from 'react-i18next';
import { isEqual } from 'lodash';
import { Box, TextFieldProps, Typography } from '@mui/material';

import { Field, FieldProps } from 'formik';
import { TextField } from 'components/form/text-field/TextField';
import { showNotification } from 'components/notification/Notification';
import { IInputComponentParams } from 'components/input-fields/helpers/interfaces';
import { NotificationType } from 'models/notification.model';
import { exceedsUniqueTokenLimit, mergeUniqueStrings, tokenizeDelimitedValues } from 'utils/arrays';
import { Env } from 'config/env';

const getTokens = (value: unknown) => (Array.isArray(value) ? (value as string[]) : []);

const getRawText = (value: unknown) => getTokens(value).join(', ');

const BulkPasteInput = ({
  field,
  form,
  meta,
  maxLength: _maxLength,
  ...props
}: IInputComponentParams & TextFieldProps & FieldProps) => {
  const [rawText, setRawText] = useState(() => getRawText(field.value));
  const ownTokensRef = useRef<string[]>(getTokens(field.value));

  const tokens = useMemo(() => mergeUniqueStrings(tokenizeDelimitedValues(rawText)), [rawText]);

  useEffect(() => {
    if (!isEqual(getTokens(field.value), ownTokensRef.current)) {
      ownTokensRef.current = getTokens(field.value);
      setRawText(getRawText(field.value));
    }
  }, [field.value]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      const nextTokens = mergeUniqueStrings(tokenizeDelimitedValues(value));

      ownTokensRef.current = nextTokens;
      setRawText(value);
      form.setFieldValue(field.name, nextTokens);
    },
    [field.name, form]
  );

  const inputField = useMemo(
    () => ({ ...field, value: rawText, onChange: handleChange }),
    [field, rawText, handleChange]
  );

  const handlePaste = useCallback(
    (event: React.ClipboardEvent<HTMLInputElement>) => {
      const input = event.target as HTMLInputElement;
      const start = input.selectionStart ?? rawText.length;
      const end = input.selectionEnd ?? start;
      const nextText = `${rawText.slice(0, start)}${event.clipboardData.getData(
        'text'
      )}${rawText.slice(end)}`;

      if (exceedsUniqueTokenLimit(nextText, Env.REACT_APP_BULK_PASTE_MAX_ITEMS)) {
        event.preventDefault();
        showNotification({
          content: t('ERRORS.BULK_PASTE_LIMIT', { max: Env.REACT_APP_BULK_PASTE_MAX_ITEMS }),
          type: NotificationType.ERROR,
        });
      }
    },
    [rawText]
  );

  return (
    <>
      <TextField
        {...props}
        field={inputField}
        form={form}
        meta={meta}
        onPaste={handlePaste}
        extraInputProps={{ autoComplete: 'off' }}
      />
      {tokens.length > 0 && (
        <Typography variant="caption" color="text.secondary">
          <Trans i18nKey="COMMON.UNIQUE_VALUE_COUNT" values={{ count: tokens.length }} />
        </Typography>
      )}
    </>
  );
};

export const LeoBulkPasteTextField: React.FC<IInputComponentParams> = ({
  name,
  variant,
  ...props
}) => {
  return (
    <Box sx={{ width: '100%' }}>
      <Field
        type="text"
        id={name}
        name={name}
        component={BulkPasteInput}
        variant={variant || Env.DEFAULT_INPUT_VARIANT}
        {...props}
      />
    </Box>
  );
};

export default LeoBulkPasteTextField;
